import { c as _c } from "react/compiler-runtime";
import type { VaultValue } from '@onefootprint/core';
import { IcoUsers16 } from '@onefootprint/icons';
import type { DataIdentifier, Entity, SameTenantDupe } from '@onefootprint/request-types/dashboard';
import { Divider, IconButton, Tooltip } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityDuplicateData from '@/components/entities/components/details/hooks/use-entity-duplicate-data';
import { getCardDis } from '@/components/entities/utils/get-dis';
import FieldOrPlaceholder from '@/components/field-or-placeholder';
import useAutoDecryptControls from '@/entity/hooks/use-auto-decrypt-controls';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import type { EntityCard } from '@/types/vault';
import getCards from '../../../../../../utils/get-cards';
import type { DiField } from '../../../../../../vault.types';
import Field from '../../../../../field';
import useGetCardIssuer from '../../utils/use-get-card-issuer';
import useGetTranslationWithoutAlias from '../../utils/use-get-translation-without-alias';
import CardDuplicateDrawer from './components/card-duplicate-drawer';
import CardSelector from './components/card-selector';
export type CardFieldsProps = {
  entity: Entity;
  setSelectedItemDis: (dis: DataIdentifier[]) => void;
};
const CardFields = t0 => {
  const $ = _c(36);
  const {
    entity,
    setSelectedItemDis
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "duplicateData.drawer.card"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== entity) {
    t2 = getCards(entity);
    $[1] = entity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cards = t2;
  const [selectedCard, setSelectedCard] = useState(cards.length > 0 ? cards[0] : undefined);
  const [duplicateDrawerOpen, setDuplicateDrawerOpen] = useState(false);
  const autoDecryptControls = useAutoDecryptControls(entity.kind);
  const decryptControls = useDecryptControls();
  const getCardIssuer = useGetCardIssuer();
  const getTranslationsWithoutAlias = useGetTranslationWithoutAlias();
  const {
    data: duplicateData,
    isLoading: isDuplicateDataLoading
  } = useEntityDuplicateData(entity.id);
  let t3;
  if ($[3] !== duplicateData?.data || $[4] !== selectedCard?.fingerprint) {
    t3 = duplicateData?.data.filter(dupe => dupe.data.some(data => data.value === selectedCard?.fingerprint));
    $[3] = duplicateData?.data;
    $[4] = selectedCard?.fingerprint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filteredDupes = t3;
  const hasDupes = !!filteredDupes?.length;
  const dis = getCardDis(entity.data, selectedCard?.alias);
  const fields = dis.map(_temp);
  let t4;
  if ($[6] !== selectedCard) {
    t4 = [selectedCard];
    $[6] = selectedCard;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(() => {
    setSelectedItemDis(dis);
  }, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setDuplicateDrawerOpen(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const openDuplicateDataDrawer = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setDuplicateDrawerOpen(false);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const closeDuplicateDataDrawer = t6;
  let t7;
  if ($[10] !== hasDupes || $[11] !== t) {
    t7 = value => {
      if (typeof value === "string") {
        return <div className="flex gap-1 items-center"><FieldOrPlaceholder data={value} />{hasDupes && <Tooltip content={t(_temp2)}><IconButton size="compact" aria-label={t(_temp3)} icon={IcoUsers16} onClick={openDuplicateDataDrawer} /></Tooltip>}</div>;
      }
      return null;
    };
    $[10] = hasDupes;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const renderCardFingerprint = t7;
  const renderCardIssuer = value_0 => {
    const changedValue = getCardIssuer(value_0);
    return <FieldOrPlaceholder data={changedValue} isLoading={autoDecryptControls.isEntityAutoDecrypting(entity.id) && decryptControls.isDecrypting(dis)} />;
  };
  let t8;
  if ($[13] !== entity || $[14] !== getTranslationsWithoutAlias || $[15] !== renderCardFingerprint || $[16] !== renderCardIssuer) {
    t8 = field => {
      const {
        di: di_0
      } = field;
      if (di_0.endsWith("fingerprint")) {
        return <Field renderValue={renderCardFingerprint} key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
      }
      if (di_0.endsWith("issuer")) {
        return <Field renderValue={renderCardIssuer} key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
      }
      return <Field key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
    };
    $[13] = entity;
    $[14] = getTranslationsWithoutAlias;
    $[15] = renderCardFingerprint;
    $[16] = renderCardIssuer;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const renderField = t8;
  const t9 = "flex flex-col gap-3";
  let t10;
  if ($[18] !== cards || $[19] !== selectedCard) {
    t10 = cards?.length > 1 && <><CardSelector cards={cards} selected={selectedCard} onChange={setSelectedCard} /><Divider /></>;
    $[18] = cards;
    $[19] = selectedCard;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== renderField) {
    t11 = field_0 => renderField(field_0);
    $[21] = renderField;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = fields.map(t11);
  let t13;
  if ($[23] !== t12) {
    t13 = <div>{t12}</div>;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== filteredDupes) {
    t14 = filteredDupes ?? [];
    $[25] = filteredDupes;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== closeDuplicateDataDrawer || $[28] !== duplicateDrawerOpen || $[29] !== isDuplicateDataLoading || $[30] !== t14) {
    t15 = <CardDuplicateDrawer isOpen={duplicateDrawerOpen} onClose={closeDuplicateDataDrawer} dupes={t14} isLoading={isDuplicateDataLoading} />;
    $[27] = closeDuplicateDataDrawer;
    $[28] = duplicateDrawerOpen;
    $[29] = isDuplicateDataLoading;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t15) {
    t16 = <div className={t9}>{t10}{t13}{t15}</div>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
};
export default CardFields;
function _temp(di) {
  return {
    di
  };
}
function _temp2($) {
  return $.cta;
}
function _temp3($_0) {
  return $_0.cta;
}